import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, CheckCircle, TrendingUp, MessageCircle,
  Share2, BarChart2, Heart, Target, Zap, Star,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PageHero from "./PageHero";
import PortfolioFooter from "./PortfolioFooter";

/* ─── Shared brand helpers ─────────────────────────────────── */
const gradientText = "bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent";
const gradientBg   = "bg-gradient-to-r from-purple-700 to-pink-600";
const iconBg       = "bg-purple-50 border border-purple-100";
const iconColor    = "text-purple-500";
const checkColor   = "text-purple-500";
const statsBg      = "bg-purple-900";
const ctaBg        = "bg-purple-950";
const sectionAlt   = "bg-purple-50";

const services = [
  {
    icon: <Share2 className={`w-7 h-7 ${iconColor}`} />,
    title: "Social Media Strategy",
    desc: "Data-driven strategies tailored to your brand voice and audience. We map the right platforms, content mix, and posting cadence to maximise reach and engagement.",
    features: ["Platform audit & competitor analysis", "Monthly content calendar", "Brand tone guidelines", "Growth roadmap"],
  },
  {
    icon: <MessageCircle className={`w-7 h-7 ${iconColor}`} />,
    title: "Content Creation & Management",
    desc: "Scroll-stopping graphics, reels, carousels, and copy crafted by our creative team. We handle scheduling, publishing, and community responses end-to-end.",
    features: ["Custom graphics & video reels", "Copywriting & hashtag research", "Scheduling & publishing", "Comment & DM management"],
  },
  {
    icon: <Target className={`w-7 h-7 ${iconColor}`} />,
    title: "Paid Social Advertising",
    desc: "Highly targeted ad campaigns on Facebook, Instagram, LinkedIn, and X that drive qualified traffic, leads, and conversions at optimal cost per result.",
    features: ["Audience targeting & lookalikes", "A/B creative testing", "Retargeting campaigns", "Budget optimisation"],
  },
  {
    icon: <BarChart2 className={`w-7 h-7 ${iconColor}`} />,
    title: "Analytics & Reporting",
    desc: "Transparent monthly reports with actionable insights — follower growth, engagement rate, reach, CTR, and ROAS — so you always know your social ROI.",
    features: ["Monthly performance reports", "KPI tracking dashboard", "Competitor benchmarking", "Strategy refinement"],
  },
];

const platforms = [
  { name: "Instagram", color: "from-pink-500 to-purple-600" },
  { name: "Facebook",  color: "from-blue-600 to-blue-800" },
  { name: "LinkedIn",  color: "from-blue-500 to-blue-700" },
  { name: "X / Twitter", color: "from-gray-700 to-gray-900" },
  { name: "YouTube",   color: "from-purple-500 to-purple-700" },
  { name: "Pinterest", color: "from-purple-600 to-rose-700" },
];

const stats = [
  { value: "3×",  label: "Average Engagement Lift", icon: <Heart className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "60%", label: "Faster Follower Growth",  icon: <TrendingUp className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "40+", label: "Brands Managed",          icon: <Star className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "24h", label: "Response SLA",             icon: <Zap className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
];

const whyUs = [
  "Creative team with deep platform expertise",
  "Dedicated account manager for your brand",
  "Content reviewed & approved before publishing",
  "Real-time campaign monitoring & adjustments",
  "Fully transparent reporting with no fluff",
  "Scalable packages for startups to enterprises",
];

const SocialMedia = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans">
      <Helmet>
        <title>Social Media Marketing | Winsil Evolution</title>
        <meta name="description" content="Grow your brand on Instagram, Facebook, LinkedIn and more. Winsil Evolution delivers creative social media strategies, content creation, paid ads and analytics." />
      </Helmet>

      <PortfolioNavbar />

      <PageHero
        label="Branding — Social Media"
        labelClass={`font-black ${gradientText}`}
        title="Social Media Marketing"
        subtitle="Build a powerful social presence that drives real engagement, loyal followers, and measurable business growth."
        image="/images/photo-1611162617213-7d7a39e9b1d7.jpg"
        bgClass="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"
        glowColor="#d946ef"
        titleClass="text-white"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>Visual Showcase</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Campaigns That Connect</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1563986768494-4dee2763ff3f.jpg", alt: "Content creator filming reel", label: "Content Creation" },
              { src: "/images/photo-1600880292203-757bb62b4baf.jpg", alt: "Social media team collaboration", label: "Community Management" },
              { src: "/images/photo-1533750349088-cd871a92f312.jpg", alt: "Social media analytics dashboard", label: "Analytics & Growth" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1596454232524-37cd53d8d2fd.jpg", alt: "Social media strategy planning", label: "Strategy Planning" },
              { src: "/images/photo-1611162617213-7d7a39e9b1d7.jpg", alt: "Mobile social media engagement", label: "Mobile Engagement" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Services Grid ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>
            What We Offer
          </p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">
            Our Social Media Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={i} className="group bg-gray-50 border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                {/* Card top accent */}
                <div className={`h-1 -mx-8 -mt-8 mb-7 rounded-t-2xl ${gradientBg}`} />
                <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:bg-purple-100 transition-colors duration-300`}>
                  {s.icon}
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center space-x-2">
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${checkColor}`} />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Platforms ── */}
      <div className={`py-16 px-4 sm:px-6 lg:px-8 ${sectionAlt}`}>
        <div className="max-w-7xl mx-auto text-center">
          <p className={`text-xs font-black uppercase tracking-[0.25em] mb-2 ${gradientText}`}>Where We Work</p>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-10">Platforms We Manage</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((p) => (
              <div key={p.name} className={`bg-gradient-to-br ${p.color} text-white px-7 py-3 rounded-full text-sm font-black shadow-md hover:scale-105 transition-transform duration-200 cursor-default`}>
                {p.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`py-16 px-4 sm:px-6 lg:px-8 ${statsBg}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s) => (
            <div key={s.label}>
              {s.icon}
              <p className="text-4xl sm:text-5xl font-black">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Why Us ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className={`text-xs font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Why Winsil</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                Social Media That{" "}
                <span className={gradientText}>Actually Grows</span> Your Brand
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We don't post for the sake of posting. Every piece of content we create is rooted
                in strategy, aligned with your goals, and designed to build a community that converts.
              </p>
              <ul className="space-y-3 mb-10">
                {whyUs.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 ${checkColor}`} />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/contact")}
                className={`inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25`}
              >
                Get a Free Proposal
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/photo-1579869847514-7c1a19d2d2ad.jpg"
                alt="Social media team at work"
                className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-pink-500/10" />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className={`text-2xl font-black ${gradientText}`}>40+</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Brands Managed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className={`relative ${ctaBg} py-16 px-4 overflow-hidden text-white text-center`}>
        <div className={`absolute inset-0 opacity-20 ${gradientBg}`} />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className={`text-[11px] font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Ready to Grow?</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Let's Elevate Your Social Presence</h2>
          <p className="text-white/55 mb-8">Tell us about your brand and we'll craft a custom strategy in 48 hours.</p>
          <button
            onClick={() => navigate("/contact")}
            className={`inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30`}
          >
            Start Your Social Journey
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default SocialMedia;
